/**
 * Base API Client
 *
 * Foundation class for all domain-specific API clients.
 * Provides authentication, request building, and error handling.
 *
 * Features:
 * - Authenticated fetch with retry logic
 * - Zod validation for runtime type safety
 * - Clear error messages on validation failures
 *
 * Note: API returns camelCase via Drizzle ORM. No transformations needed.
 */

import type { ZodType } from 'zod/v4';

type ZodSchema<T = unknown> = ZodType<T>;
import { API_BASE_URL } from './config';
import { validateApiResponse } from './validation';
import { logError } from '@/lib/logger';
import {
  ApiContractError,
  type AuthenticatedFetchOptions,
  type ExpectedRefusal,
} from '@/lib/fetch-utils';

export type { AuthenticatedFetchOptions, ExpectedRefusal };

/**
 * Type for the authenticated fetch function from useAuthFetch hook
 */
export type AuthenticatedFetch = <T>(
  url: string,
  options?: AuthenticatedFetchOptions,
  maxRetries?: number
) => Promise<T>;

/**
 * Base API Client class
 *
 * All domain API clients extend this class to inherit:
 * - Authenticated fetch with retry logic
 * - URL building with base URL
 * - Consistent request/response handling
 */
export class BaseApiClient {
  protected baseUrl: string;

  constructor(
    protected authenticatedFetch: AuthenticatedFetch,
    baseUrl?: string
  ) {
    this.baseUrl = baseUrl ?? API_BASE_URL;
  }

  /**
   * Build full URL with base URL prefix
   */
  protected url(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  /**
   * Make an authenticated request
   *
   * @param path - API endpoint path (without base URL)
   * @param options - Request options (method, body, etc.)
   * @param maxRetries - Optional max retries for transient errors
   */
  protected request<T>(
    path: string,
    options?: AuthenticatedFetchOptions,
    maxRetries?: number
  ): Promise<T> {
    const fullUrl = this.url(path);
    if (maxRetries !== undefined) {
      return this.authenticatedFetch<T>(fullUrl, options, maxRetries);
    }
    if (options !== undefined) {
      return this.authenticatedFetch<T>(fullUrl, options);
    }
    return this.authenticatedFetch<T>(fullUrl);
  }

  /**
   * Options for a read that asks the DEMO of a closed feature (#2816). The
   * demo answer and the organization's own one share a URL, and a route that
   * sends a browser-cacheable `Cache-Control` (the Voice of Customer section)
   * leaves the browser free to answer a demo ask from an entry stored under
   * the plan the organization has just left — an answer without `demoOnly`,
   * drawn as the customer's own. A server-side `no-store` cannot help: the
   * request never reaches the server. So the demo ask bypasses the browser
   * cache (and stores nothing); the own ask keeps whatever the route allows.
   */
  protected demoAskOptions(
    demo: boolean | undefined
  ): AuthenticatedFetchOptions | undefined {
    return demo ? { cache: 'no-store' } : undefined;
  }

  /**
   * Build query string from params object
   *
   * An array value (e.g. `projectIds`) is CSV-joined — the transport the
   * backend's shared `projectIdsFilterSchema` parses (project-multiselect
   * K7). An empty array is treated the same as `undefined` (omitted
   * entirely) so a cleared multi-select never sends `?projectIds=`, which
   * the backend would otherwise have to special-case.
   */
  protected buildQuery(
    params?: Record<string, string | number | boolean | string[] | undefined>
  ): string {
    if (!params) return '';

    const queryParams: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined) continue;
      if (Array.isArray(value)) {
        if (value.length > 0) queryParams[key] = value.join(',');
        continue;
      }
      queryParams[key] = String(value);
    }

    return Object.keys(queryParams).length > 0
      ? `?${new URLSearchParams(queryParams).toString()}`
      : '';
  }

  /**
   * Make a validated request with Zod schema
   *
   * Validates API response with Zod for runtime type safety.
   * Logs clear errors if validation fails.
   *
   * @param path - API endpoint path
   * @param schema - Zod schema to validate response
   * @param options - Request options
   * @returns Validated and typed response, or throws on validation failure
   *
   * @example
   * ```ts
   * const record = await this.validatedRequest(
   *   '/api/v1/records/123',
   *   RecordResponseSchema
   * );
   * // record is fully typed and validated!
   * ```
   */
  protected async validatedRequest<T>(
    path: string,
    schema: ZodSchema<T>,
    options?: AuthenticatedFetchOptions,
    maxRetries?: number
  ): Promise<T> {
    const fullUrl = this.url(path);

    // Fetch raw data
    const rawData = await (maxRetries !== undefined
      ? this.authenticatedFetch<unknown>(fullUrl, options, maxRetries)
      : options !== undefined
        ? this.authenticatedFetch<unknown>(fullUrl, options)
        : this.authenticatedFetch<unknown>(fullUrl));

    // Validate with Zod (API returns camelCase via Drizzle)
    const result = validateApiResponse(schema, rawData, {
      endpoint: path,
      throwOnError: import.meta.env.DEV, // Throw in dev, log in prod
    });

    if (!result.success) {
      // NO SILENT FAILURES: Log to Sentry and throw
      const errorDetails = {
        endpoint: path,
        issues: result.error?.issues.map((i) => ({
          path: i.path.join('.'),
          message: i.message,
          code: i.code,
        })),
      };

      logError('API validation failed', {
        component: 'BaseApiClient.validatedRequest',
        ...errorDetails,
      });

      throw new ApiContractError(
        `API validation failed for ${path}: ${JSON.stringify(errorDetails.issues)}`
      );
    }

    return result.data;
  }
}
